import React, {useCallback, useEffect, useState} from 'react';
import {Alert, Chip, MenuItem, Tab, Tabs, TextField, Tooltip} from '@mui/material';
import PropTypes from 'prop-types';
import {DataGridPro} from '@mui/x-data-grid-pro';
import {useNavigate} from 'react-router-dom';
import authenticationManager from '../../Auth/AuthenticationManager';
import {redirectToLogin} from '../../Auth/loginRedirect';
import SmsLogApiService from '../../api/SmsLogApiService';
import SettingsApiService from '../../api/SettingsApiService';
import PageShell from '../PageShell';
import {
    getInboundActionLabel,
    getSmsCategoryLabel,
    getSmsStatusColour,
    getSmsStatusLabel,
    STATUS_FILTER_OPTIONS,
} from '../../utilities/SmsStatus';

const PAGE_SIZE = 50;

/** How long to wait after the last keystroke before searching, so typing does not hit the server per character. */
const SEARCH_DEBOUNCE_MS = 400;

function formatTimestamp(value) {
    return value ? new Date(value).toLocaleString() : '';
}

export default function SmsMessageLog(props) {
    const {setSnackbarProperties, isMobile} = props;

    const smsLogApiService = new SmsLogApiService();
    const settingsApiService = new SettingsApiService();
    const navigate = useNavigate();

    const [tab, setTab] = useState('outbound');
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [status, setStatus] = useState('');
    const [page, setPage] = useState(0);
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isSmsEnabled, setIsSmsEnabled] = useState(true);

    const outboundColumns = [
        {
            field: 'createdUtc',
            headerName: 'Queued',
            flex: 1,
            valueFormatter: (params) => formatTimestamp(params.value),
        },
        {field: 'tenantName', headerName: 'Tenant', flex: 1},
        {field: 'to', headerName: 'To', flex: 1},
        {
            field: 'category',
            headerName: 'Type',
            flex: 1,
            valueFormatter: (params) => getSmsCategoryLabel(params.value),
        },
        {
            field: 'status',
            headerName: 'Delivery',
            flex: 1,
            renderCell: (params) => (
                // The provider's own wording and error code sit behind the chip: they are what makes
                // a failure searchable in Twilio's console, but they would crowd the grid.
                <Tooltip
                    title={[params.row.providerStatus && `Twilio: ${params.row.providerStatus}`,
                        params.row.providerErrorCode && `Error ${params.row.providerErrorCode}`,
                        params.row.lastErrorMessage]
                        .filter(Boolean).join(' — ') || ''}
                >
                    <Chip
                        size={'small'}
                        label={getSmsStatusLabel(params.value)}
                        color={getSmsStatusColour(params.value)}
                    />
                </Tooltip>
            ),
        },
        {
            field: 'deliveredUtc',
            headerName: 'Delivered',
            flex: 1,
            valueFormatter: (params) => formatTimestamp(params.value),
        },
        {field: 'body', headerName: 'Message', flex: 2},
    ];

    const inboundColumns = [
        {
            field: 'receivedUtc',
            headerName: 'Received',
            flex: 1,
            valueFormatter: (params) => formatTimestamp(params.value),
        },
        {field: 'tenantName', headerName: 'Tenant', flex: 1},
        {field: 'fromNumber', headerName: 'From', flex: 1},
        {
            field: 'action',
            headerName: 'Interpreted As',
            flex: 1,
            valueFormatter: (params) => getInboundActionLabel(params.value),
        },
        {field: 'body', headerName: 'Message', flex: 2},
    ];

    useEffect(() => {
        if (!authenticationManager.isAuthorized) {
            redirectToLogin(navigate);
            return;
        }

        if (!authenticationManager.isSuperAdmin()) {
            navigate('/');
            return;
        }

        document.title = 'Executive Storage Suites - Message Log';

        settingsApiService.isSmsEnabled().then(setIsSmsEnabled);
    }, []);

    // Debounced so each keystroke does not run a LIKE query across the largest table in the database.
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(0);
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [search]);

    const loadMessages = useCallback(async () => {
        setLoading(true);

        const query = {search: debouncedSearch, page: page + 1, pageSize: PAGE_SIZE};

        const response = tab === 'outbound'
            ? await smsLogApiService.searchOutbound({...query, status: status === '' ? null : status})
            : await smsLogApiService.searchInbound(query);

        if (response?.success) {
            setRows(response.data.items);
            setRowCount(response.data.totalCount);
        } else {
            setSnackbarProperties(`Error loading the message log: ${response?.message}`, 'error');
            setRows([]);
            setRowCount(0);
        }

        setLoading(false);
    }, [tab, debouncedSearch, status, page]);

    useEffect(() => {
        void loadMessages();
    }, [loadMessages]);

    function changeTab(newTab) {
        setTab(newTab);
        setPage(0);
        setRows([]);
    }

    return (
        <PageShell
            title={'Message Log'}
            subtitle={`${rowCount} message${rowCount === 1 ? '' : 's'}`}
            isMobile={isMobile}
            actions={
                <Tabs value={tab} onChange={(event, newTab) => changeTab(newTab)}>
                    <Tab value={'outbound'} label={'Sent'} />
                    <Tab value={'inbound'} label={'Received'} />
                </Tabs>
            }
            toolbar={
                <>
                    <TextField
                        size={'small'}
                        label={'Search'}
                        placeholder={'Number, message text or tenant name'}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        sx={{minWidth: isMobile ? '100%' : '320px'}}
                    />
                    {tab === 'outbound' &&
                        <TextField
                            select
                            size={'small'}
                            label={'Delivery status'}
                            value={status}
                            onChange={(event) => {
                                setStatus(event.target.value);
                                setPage(0);
                            }}
                            sx={{minWidth: isMobile ? '100%' : '220px'}}
                        >
                            {STATUS_FILTER_OPTIONS.map((option) => (
                                <MenuItem key={option.label} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </TextField>
                    }
                </>
            }
        >
            {!isSmsEnabled &&
                <Alert severity={'info'} sx={{margin: '16px'}}>
                    Text messaging is switched off, so nothing new is being sent. This log shows what was sent
                    previously.
                </Alert>
            }

            <DataGridPro
                rows={rows}
                columns={tab === 'outbound' ? outboundColumns : inboundColumns}
                getRowId={(row) => row.id}
                loading={loading}
                autoHeight
                pagination
                paginationMode={'server'}
                rowCount={rowCount}
                pageSizeOptions={[PAGE_SIZE]}
                paginationModel={{page, pageSize: PAGE_SIZE}}
                onPaginationModelChange={(model) => setPage(model.page)}
            />
        </PageShell>
    );
}

SmsMessageLog.propTypes = {
    setSnackbarProperties: PropTypes.func,
    isMobile: PropTypes.bool,
};
